import {
  ActionButton,
  OfficeButton,
  OfficeHeader,
  Paginator,
  TableComponent,
} from "components";
import { useNavigate, useSearch } from "react-location";
import { useUrlState, withPermissions, wrapClick } from "utils";

import CreateUserRoleContainer from "./create";
import { LocationGenerics } from "router/location";
import { PlusIcon } from "@heroicons/react/24/outline";
import UpdateUserRoleContainer from "./update";
import { UserCog2Icon } from "lucide-react";
import ViewRoleContainer from "./view";
import { useLocalTableData } from "hooks/ui/use-local-table-data";
import { usePagination } from "hooks";
import { useRoles } from "hooks/data/use-roles";

type Props = {};

const SettingsPage = (props: Props) => {
  const [modal, setModal] = useUrlState("modal");
  const { roles, loading, refetch } = useRoles();
  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const { offset, limit, setPage, search } = usePagination();
  const { paginatedData, total } = useLocalTableData({
    data: roles,
    limit: 10,
    offset: 0,
    search: search,
  });

  return (
    <>
      <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
        <OfficeHeader
          renderActions={() => (
            <>
              {withPermissions([
                "*:*",
                "employees:*",
                "employees:assign",
                "employees:manage",
                "employees:create",
              ])(
                <OfficeButton
                  type="button"
                  onClick={wrapClick(() => {
                    setModal("create");
                  })}
                  size="sm"
                >
                  <PlusIcon
                    className="-ml-0.5 mr-2 h-4 w-4"
                    aria-hidden="true"
                  />
                  Create User Role
                </OfficeButton>
              )}
            </>
          )}
        />
        <div className="flex flex-1 overflow-y-auto">
          <div className="flex-1 mx-auto  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
            <TableComponent
              title={"User Roles"}
              loading={loading}
              hasSearch
              data={paginatedData}
              columns={[
                {
                  accessor: "position",
                  name: "position",
                },
                {
                  accessor: "actions",
                  name: "actions",
                  render: (item) => (
                    <div className="flex justify-start items-center gap-2">
                      {withPermissions([
                        "*:*",
                        "employees:*",
                        "employees:assign",
                        "employees:manage",
                      ])(
                        <ActionButton
                          action="view"
                          onClick={() => {
                            navigate({
                              search(prev) {
                                return {
                                  ...prev,
                                  id: item?.user_role_id as string,
                                  modal: "view",
                                };
                              },
                            });
                          }}
                        />
                      )}
                      {withPermissions([
                        "*:*",
                        "employees:*",
                        "employees:assign",
                        "employees:manage",
                        "employees:update",
                      ])(
                        <ActionButton
                          action="update"
                          onClick={() => {
                            navigate({
                              search(prev) {
                                return {
                                  ...prev,
                                  id: item?.user_role_id as string,
                                  modal: "update",
                                };
                              },
                            });
                          }}
                        />
                      )}
                    </div>
                  ),
                },
              ]}
              refetch={refetch}
              defaultView="grid"
              renderGridItem={({ item }) => (
                <div className="p-5 bg-card-500 border border-card-400 rounded-lg shadow-sm hover:shadow-md transition-transform transform hover:-translate-y-1 flex flex-col justify-center items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                    <UserCog2Icon className="h-6 w-6 text-primary" />
                  </div>

                  <h3 className="text-sm font-semibold text-primary">
                    {item?.position}
                  </h3>

                  {/* {item?.description && (
                                        <p className="text-xs text-gray-500 text-center">
                                            {item.description}
                                        </p>
                                    )} */}

                  <div className="w-full flex justify-center items-center gap-3 mt-2">
                    {withPermissions([
                      "*:*",
                      "employees:*",
                      "employees:assign",
                      "employees:manage",
                    ])(
                      <ActionButton
                        action="view"
                        onClick={() => {
                          navigate({
                            search(prev) {
                              return {
                                ...prev,
                                id: item?.user_role_id as string,
                                modal: "view",
                              };
                            },
                          });
                        }}
                      />
                    )}
                    {withPermissions([
                      "*:*",
                      "employees:*",
                      "employees:assign",
                      "employees:manage",
                      "employees:update",
                    ])(
                      <ActionButton
                        action="update"
                        onClick={() => {
                          navigate({
                            search(prev) {
                              return {
                                ...prev,
                                id: item?.user_role_id as string,
                                modal: "update",
                              };
                            },
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
              pagination={() => (
                <Paginator
                  offset={offset}
                  limit={limit}
                  totalItems={total}
                  currentSize={paginatedData.length}
                  setOffset={setPage}
                />
              )}
            />
          </div>
        </div>
      </main>

      <CreateUserRoleContainer
        open={modal === "create"}
        setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
        refetch={refetch}
      />

      {searchParams?.id && (
        <UpdateUserRoleContainer
          open={modal === "update"}
          setOpen={(val: boolean) =>
            navigate({
              search(prev) {
                return {
                  ...prev,
                  modal: val ? "update" : undefined,
                  id: val ? (searchParams?.id as string) : undefined,
                };
              },
            })
          }
          // refetch={refetch}
        />
      )}
      {searchParams?.id && modal === "view" && (
        <ViewRoleContainer
          open={modal === "view"}
          setOpen={(val: boolean) =>
            navigate({
              search(prev) {
                return {
                  ...prev,
                  modal: val ? "view" : undefined,
                  id: val ? (searchParams?.id as string) : undefined,
                };
              },
            })
          }
        />
      )}
    </>
  );
};

export default SettingsPage;
